import { useEffect, useState } from 'react'
import { HostingsAPI, OrdersAPI } from '../api'
import { Check, ChevronDown, X } from 'lucide-react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'
import { useComparison } from './ComparisonContext'
import { SafeHtml } from './SafeHtml'

const ExpandableSection = ({ title, content }: { title: React.ReactNode, content?: string | null }) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!content) return (
    <div className="py-1">
      <div className="flex items-center justify-between p-2 -mx-2">
        <div className="flex-1 dark:text-gray-200">{title}</div>
      </div>
    </div>
  );

  return (
    <div className="py-1">
      <div
        className="flex items-center justify-between cursor-pointer group select-none p-2 -mx-2 rounded-lg bg-gray-50 hover:bg-gray-100 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        <div className="flex-1 dark:text-gray-200">{title}</div>
        <div className="text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ml-[10px]">
          <div className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <SafeHtml
            html={content}
            mode="rich"
            className="mt-2 text-sm text-gray-600 dark:text-gray-400 pl-2 border-l-2 border-gray-100 dark:border-slate-700 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 ml-2"
          />
        </div>
      </div>
    </div>
  )
}

const getBandwidthPercent = (bandwidth: string | null) => {
  if (!bandwidth) return 100
  const bw = bandwidth.toLowerCase()
  if (bw.includes('ilimitado') || bw.includes('sin límite') || bw.includes('sin limite') || bw.includes('sin límites')) return 100

  const match = bw.match(/([\d,.]+)\s*(tb|gb|mb)?/)
  if (!match) return 0

  let value = parseFloat(match[1].replace(',', '.'))
  const unit = match[2]

  if (unit === 'tb') value *= 1000
  else if (unit === 'mb') value /= 1000

  // 1500GB is 100%
  return Math.min((value / 1500) * 100, 100)
}

const HostingPage = () => {
  const [plans, setPlans] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<'ANUAL' | 'MENSUAL'>('ANUAL')
  const [selectingId, setSelectingId] = useState<string | null>(null)

  const { isSignedIn, user } = useUser()
  const clerk = useClerk()
  const navigate = useNavigate()
  const { selectedPlans, togglePlan, isInComparison, clearSelection } = useComparison()

  useEffect(() => {
    window.scrollTo(0, 0)
    const fetchPlans = async () => {
      // 1. Cargar caché local para visualización inmediata
      const CACHE_KEY = 'hosting_plans_v1'
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        try {
          setPlans(JSON.parse(cached))
          setLoading(false)
        } catch (e) {
          console.error('Error parsing cache:', e)
        }
      }

      try {
        // 2. Obtener datos frescos de la API
        const res = await HostingsAPI.list()
        if (res.success && res.data) {
          const activePlans = res.data
            .filter((p: any) => p.displayName && p.isVisible !== false)
            .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))

          // 3. Actualizar estado y caché solo si hay cambios
          const newCache = JSON.stringify(activePlans)
          if (newCache !== cached) {
            setPlans(activePlans)
            localStorage.setItem(CACHE_KEY, newCache)
          }
        }
      } catch (error) {
        console.error('Error fetching plans:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchPlans()
  }, [])

  const filteredPlans = plans.filter(p => {
    if (period === 'ANUAL') return p.payType === 'ANUAL'
    if (period === 'MENSUAL') return p.payType === 'MENSUAL'
    return false
  })

  const Feature = ({ label, value, icon: Icon }: any) => (
    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-1">
      {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      <span className="font-medium">{label}:</span>
      <span>{value}</span>
    </div>
  )

  const handleSelectPlan = async (plan: any) => {
    if (!isSignedIn) {
      clerk.openSignIn()
      return
    }

    if (!user) return

    setSelectingId(plan.id)

    try {
      const res = await OrdersAPI.create(
        user.id,
        plan.id,
        {
          userEmail: user.primaryEmailAddress?.emailAddress || '',
          userName: user.fullName || user.firstName || 'Usuario',
          planName: plan.displayName,
          planPrice: plan.price.toString(),
          planCurrency: plan.currency,
          planPeriod: plan.payTypeDisplay || (plan.payType === 'ANUAL' ? 'Anual' : 'Mensual')
        },
        undefined // No domain initially
      )
      if (res.success) {
        navigate('/dashboard')
      } else {
        console.error('Error selecting plan:', res.error)
        alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.')
      }
    } catch (error) {
      console.error('Error selecting plan:', error)
      alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.')
    } finally {
      setSelectingId(null)
    }
  }

  if (loading) return (
    <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 pt-12 pb-20">
      {/* Header / Banner */}
      <div className="w-full text-center text-gray-900 dark:text-white mb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
                Nuestros Planes de Hosting
            </h1>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto">
                Elige el plan perfecto para tu proyecto web. Escalabilidad, velocidad y seguridad garantizadas.
            </p>
        </div>
      </div>

      <div className="w-full relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white dark:bg-slate-800 rounded-full p-1 shadow-sm border border-gray-100 dark:border-slate-700 inline-flex">
              <button
                onClick={() => setPeriod('ANUAL')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${period === 'ANUAL'
                  ? 'bg-[#0F172A] dark:bg-slate-900 text-white shadow-md'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Anual
              </button>
              <button
                onClick={() => setPeriod('MENSUAL')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${period === 'MENSUAL'
                  ? 'bg-[#0F172A] dark:bg-slate-900 text-white shadow-md'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Mensual
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
            {filteredPlans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => navigate(`/hosting/${encodeURIComponent(plan.name)}`)}
                className="bg-white dark:bg-slate-800 rounded-t-[2rem] rounded-tr-[10rem] rounded-b-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.20)] dark:shadow-none dark:hover:shadow-none border border-gray-100 dark:border-slate-700 relative overflow-visible transition-shadow duration-300 flex flex-col cursor-pointer"
              >

                {/* Background Icon/Image positioned absolute top-right */}
                <div className="absolute top-0 right-0 pointer-events-none z-0">
                  {plan.mainPhotoUrl ? (
                    <img src={plan.mainPhotoUrl} alt={`Icono de ${plan.displayName}`} className="w-[130px] h-[130px] object-contain" />
                  ) : (
                    <div className="w-[130px] h-[130px] rounded-bl-[4rem] bg-gray-50 flex items-center justify-center text-gray-300 font-bold text-4xl">
                      {plan.displayName.substring(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="p-8 pb-4 flex-1 relative z-10">
                  {/* Header */}
                  <div className="relative z-10 mb-8 pr-28">
                    <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 leading-tight">{plan.displayName}</h3>
                  </div>

                  {/* Price */}
                  <div className="relative z-10 mb-8">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">{plan.currency}</span>
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-1">{plan.payTypeDisplay || (plan.payType.toLowerCase() === 'anual' ? 'por año' : 'por mes')}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-4">
                    <ExpandableSection
                      title={
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                          <span className="font-medium">Recursos:</span>
                          <span>{plan.resources === 'COMPARTIDOS' ? 'Compartidos' : 'Dedicados'}</span>
                        </div>
                      }
                      content={plan.resourcesDetail}
                    />

                    <ExpandableSection
                      title={
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-200">Elegir Datacenter:</span>
                          <span className={plan.dataCenterSelectable ? 'text-blue-600 font-bold dark:text-blue-400' : 'text-gray-500 dark:text-gray-300'}>
                            {plan.dataCenterSelectable ? 'sí' : 'no'}
                          </span>
                        </div>
                      }
                      content={plan.dataCenterDetail}
                    />

                    <Feature label="Sitios máximos" value={plan.sites === 'ONE' ? '1' : plan.sites === 'TWO' ? '2' : 'Múltiples'} />
                    <Feature label="Sistema" value={plan.hostingSystem === 'APACHE' ? 'Apache' : plan.hostingSystem === 'NGINX' ? 'Nginx' : 'Otro'} />

                    <div className="flex items-center gap-2 text-sm py-1">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Certificado SSL gratis</span>
                      {plan.ssl && <Check className="w-4 h-4 text-green-500" />}
                    </div>

                    {/* Space Bar */}
                    <div className="mt-4 mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold text-gray-900 dark:text-gray-100">Espacio: {plan.spaceGb} GB</span>
                        <span className="text-gray-400 text-xs">de 50 GB</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${(plan.spaceGb / 50) * 100 < 25 ? 'bg-red-500' :
                            (plan.spaceGb / 50) * 100 < 50 ? 'bg-orange-500' :
                              (plan.spaceGb / 50) * 100 < 80 ? 'bg-green-500' : 'bg-green-600'
                            }`}
                          style={{ width: `${Math.min((plan.spaceGb / 50) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Bandwidth Bar */}
                    <div className="mt-2 mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-bold text-gray-900 dark:text-gray-100">Bandwidth: {plan.bandwidth || 'Ilimitado'}</span>
                        <span className="text-gray-400 text-xs">de Max.</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                        {(() => {
                          const percent = getBandwidthPercent(plan.bandwidth)
                          return (
                            <div
                              className={`h-1.5 rounded-full ${percent < 25 ? 'bg-red-500' :
                                percent < 50 ? 'bg-orange-500' :
                                  percent < 80 ? 'bg-green-500' : 'bg-green-600'
                                }`}
                              style={{ width: `${percent}%` }}
                            ></div>
                          )
                        })()}
                      </div>
                    </div>

                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {plan.emails === 0 ? 'Sin cuentas de email' : `${plan.emails} Cuentas de email`}
                    </div>

                    <ExpandableSection
                      title={
                        <div className="flex items-center gap-2 text-sm w-full">
                          <span className="font-medium text-gray-700 dark:text-gray-200">Respaldos:</span>
                          <span className={plan.backups ? 'text-blue-600 font-bold dark:text-blue-400' : 'font-bold text-gray-500 dark:text-gray-100'}>
                            {plan.backups ? 'sí' : 'no'}
                          </span>
                        </div>
                      }
                      content={plan.backupsDetail}
                    />

                    <ExpandableSection
                      title={
                        <div className="space-y-1">
                          <span className="font-bold text-sm text-gray-900 dark:text-gray-100">Seguridad {plan.security === 'NONE' ? 'No' : plan.security === 'BASIC' ? 'Básica' : plan.security === 'MEDIUM' ? 'Media' : 'Avanzada'}</span>
                          <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${plan.security === 'NONE' ? 'bg-red-500 w-[10%]' :
                                plan.security === 'BASIC' ? 'bg-orange-500 w-[30%]' :
                                  plan.security === 'MEDIUM' ? 'bg-green-500 w-[60%]' : 'bg-green-600 w-[100%]'
                                }`}
                            ></div>
                          </div>
                        </div>
                      }
                      content={plan.securityDetail}
                    />

                    <div className="flex items-center gap-2 text-sm pt-2">
                      <span className="text-gray-500">Cache:</span>
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {plan.cache === 'NONE' ? 'No' :
                          plan.cache === 'SI' ? 'Sí' :
                            plan.cache === 'DINAMICA' ? 'Dinámica' :
                              plan.cache === 'OBJETOS' ? 'De objetos' : 'Dinámica y Objetos'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">Opt. de imágenes:</span>
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {plan.imageOptimization === 'NONE' ? 'no' :
                          plan.imageOptimization === 'SI' ? 'si' : 'Smush Pro'}
                      </span>
                    </div>

                  </div>
                </div>

                {/* Footer Area */}
                <div className="bg-gray-50 dark:bg-slate-900/50 p-6 mt-auto border-t border-gray-100 dark:border-slate-700/50 min-h-[80px] rounded-b-lg flex flex-col [@media(min-width:350px)]:flex-row items-center justify-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlan(plan);
                    }}
                    className={`w-full font-medium py-3 px-6 rounded-full border transition-all duration-300 ${isInComparison(plan.id)
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'
                      }`}
                  >
                  {isInComparison(plan.id) ? 'Seleccionado' : 'Comparar'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSelectPlan(plan)
                    }}
                    disabled={selectingId === plan.id}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {selectingId === plan.id ? 'Procesando...' : 'Elegir plan'}
                  </button>
                </div>
              </div>
            ))}
            {filteredPlans.length === 0 && (
              <div className="col-span-full text-center text-gray-500 py-12">
                No hay planes disponibles para el periodo seleccionado.
              </div>
            )}
          </div>

          {/* Comparison Floating Bar */}
          {selectedPlans.length > 0 && (
            <div className="fixed [@media(max-width:449px)]:bottom-0 [@media(min-width:450px)]:bottom-6 left-1/2 transform -translate-x-1/2 z-50 bg-white dark:bg-slate-800 shadow-2xl rounded-full [@media(max-width:449px)]:rounded-t-2xl [@media(max-width:449px)]:rounded-b-none px-6 py-3 [@media(max-width:449px)]:py-6 flex [@media(max-width:449px)]:flex-col items-center gap-6 [@media(max-width:449px)]:gap-5 border border-gray-200 dark:border-slate-700 animate-in slide-in-from-bottom-5 fade-in duration-300 [@media(max-width:449px)]:w-full [@media(min-width:450px)]:w-fit">
              
              {/* Desktop: Borrar todo button */}
              <button
                onClick={clearSelection}
                className="hidden [@media(min-width:450px)]:block mr-2 text-gray-400 hover:text-red-500 transition-colors"
                title="Borrar todo"
              >
                <div className="flex flex-col items-center">
                  <span className="text-xs font-bold text-red-500 mb-1">Borrar todo</span>
                </div>
              </button>

              {/* Plan squares container */}
              <div className="flex items-center gap-4 [@media(max-width:449px)]:gap-3">
                {selectedPlans.map(p => (
                  <div key={p.id} className="relative w-10 h-10 [@media(max-width:449px)]:w-12 [@media(max-width:449px)]:h-12 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center p-1 border border-gray-200 dark:border-slate-600">
                    <button
                      onClick={() => togglePlan(p)}
                      className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-0.5 hover:bg-red-500 transition-colors"
                    >
                      <X size={12} />
                    </button>
                    {p.mainPhotoUrl ? (
                      <img src={p.mainPhotoUrl} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <span className="font-bold text-xs text-gray-400">{p.displayName.substring(0, 2)}</span>
                    )}
                  </div>
                ))}

                {Array.from({ length: Math.max(0, 4 - selectedPlans.length) }).map((_, i) => (
                  <div key={i} className="w-10 h-10 [@media(max-width:449px)]:w-12 [@media(max-width:449px)]:h-12 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg"></div>
                ))}
              </div>

              {/* Desktop: Analizar button */}
              <button
                onClick={() => navigate('/comparar')}
                className="hidden [@media(min-width:450px)]:block bg-[#583395] hover:bg-[#482a7a] text-white px-6 py-2 rounded-full font-medium shadow-md transition-colors"
              >
                Analizar
              </button>

              {/* Mobile: Split Button Group (Borrar + Analizar) */}
              <div className="[@media(min-width:450px)]:hidden flex w-full rounded-full overflow-hidden shadow-sm mt-2">
                <button
                  onClick={clearSelection}
                  className="flex-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 py-3 font-bold text-sm hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors border-r border-gray-300 dark:border-slate-600"
                >
                  Borrar
                </button>
                <button
                  onClick={() => navigate('/comparar')}
                  className="flex-1 bg-[#583395] text-white py-3 font-bold text-sm hover:bg-[#482a7a] transition-colors"
                >
                  Analizar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HostingPage